<template>
    <div
        :class="[
            'base-input-wrap',
            {'with-error-message': props.withErrorMessage},
            {'has-bg': props.bg},
        ]"
    >
        <input
            :class="[
                'base-input',
                {
                    'has-icon': hasIconSlot,
                    'base-input--invalid': hasError,
                    'has-eye-icon': hasShowPasswordIcon,
                },
            ]"
            :type="type"
            :name="name"
            v-bind="$attrs"
            :value="modelValue"
            @input="inputHandler"
        />

        <div v-show="hasShowPasswordIcon">
            <SvgEyeClosed
                v-if="isEyeClosed"
                class="icon-eye"
                @click="showPassword"
            />
            <SvgEye v-else class="icon-eye open" @click="showPassword" />
        </div>

        <div v-if="hasIconSlot" class="icon" @click="reset">
            <slot name="icon"></slot>
        </div>

        <ErrorMessage
            v-if="props.hasError && props.name"
            :name="props.name"
            class="error-message"
            as="div"
        />
    </div>
</template>

<script setup lang="ts">
import {computed, PropType, useSlots} from 'vue';
import {nonNumericReg} from '@/utils/regex';
import {ErrorMessage} from 'vee-validate';
import SvgEye from '@/assets/svg/icn_eye.svg?component';
import SvgEyeClosed from '@/assets/svg/icn_eye_closed.svg?component';
import {ref} from 'vue';
// props, context, emits, libs
const props = defineProps({
    modelValue: {
        type: [String, Number],
        required: true,
    },
    type: {
        type: String,
        default: 'text',
    },
    maxLength: {
        type: Number,
    },
    hasError: {
        type: Boolean,
        default: false,
    },
    name: {
        type: String,
    },
    withErrorMessage: {
        type: Boolean,
        default: false,
    },
    pattern: {
        type: Object as PropType<RegExp | null>,
        default: null,
    },
    bg: {
        type: Boolean,
        default: false,
    },
    hasShowPasswordIcon: {
        type: Boolean,
        default: false,
    },
});

const emits = defineEmits(['update:modelValue', 'showPassword']);
const slots = useSlots();
const hasIconSlot = computed(() => !!slots.icon);

// ref, reactive
// computed
// methods
const inputHandler = (e: any) => {
    const currentLength = e.target.value.length;

    if (props.maxLength && currentLength >= props.maxLength) {
        e.target.value = e.target.value.slice(0, props.maxLength);
    }

    // pattern: 커스텀 정규식 필터
    if (props.pattern) {
        e.target.value = e.target.value.replace(props.pattern, '');
    }

    if (props.type === 'number') {
        e.target.value = e.target.value.replace(nonNumericReg, '');
    }

    if (typeof props.modelValue === 'number') {
        emits('update:modelValue', Number(e.target.value));
    }

    emits('update:modelValue', e.target.value);
};

const reset = () => {
    emits('update:modelValue', '');
};

const isEyeClosed = ref(false);
const showPassword = () => {
    emits('showPassword');
    isEyeClosed.value = !isEyeClosed.value;
};

// effect (created, mounted, watch, VueUse)
</script>

<style lang="scss" scoped>
.base-input-wrap {
    position: relative;
    height: fit-content;
    border: 0;

    &.has-bg {
        input {
            max-width: 300px;
            background: $bg-field;
            border: none;
        }
    }

    &.with-error-message {
        .error-message {
            position: absolute;
            left: 0;
            width: 100%;
            min-height: 18px;
        }
    }

    .base-input {
        outline: none;
        width: 100%;
        height: 40px;
        padding: 0 12px;
        border: 1px solid $black_16;
        border-radius: 6px;

        &.has-icon {
            padding: 0 40px 0 12px;
        }

        &.has-eye-icon {
            padding-right: 40px;
        }

        &--invalid {
            border-color: $danger;
        }

        &:hover:not(:read-only) {
            border: 1px solid $black_40;
            cursor: text;
        }

        &:focus:not(:read-only) {
            border: 1px solid $primary;
            cursor: text;
        }

        &::placeholder {
            color: rgba(0, 0, 0, 0.3);
        }

        &:disabled {
            color: rgba(0, 0, 0, 0.16);
            background-color: rgba(0, 0, 0, 0.08);
            cursor: default;
        }
    }

    .icon,
    .icon-eye {
        position: absolute;
        right: 0;
        top: 0;
        margin: 10px;
        flex: 0 0 40px;
        cursor: pointer;
    }

    .icon-eye {
        :deep(path) {
            fill: #adadad;
        }
        &.open {
            :deep(path) {
                fill: $NDS44;
            }
        }
    }
}
</style>
